import type { Template } from "../types/templates.types";

export const cleaningServicesInit: Template = {
  id: "cleaning-services",
  name: "Professional Cleaning Services",
  description: "Complete landing page template for professional cleaning service business. Includes header, hero section, services showcase, pricing, testimonials, and footer.",
  html: `
    <!-- Header -->
    <header class="cs-header">
      <div class="cs-container">
        <div class="cs-logo">CleanPro</div>
        <nav class="cs-nav">
          <a href="#services">Services</a>
          <a href="#pricing">Pricing</a>
          <a href="#testimonials">Testimonials</a>
          <a href="#contact">Contact</a>
        </nav>
        <button class="cs-cta-btn">Get Free Quote</button>
      </div>
    </header>

    <!-- Hero Section -->
    <section class="cs-hero">
      <div class="cs-container">
        <h1>Professional Cleaning Services for Your Home</h1>
        <p>We provide top-quality cleaning services with attention to detail and customer satisfaction guaranteed.</p>
        <button class="cs-btn-primary">Book Now</button>
      </div>
    </section>

    <!-- Services Section -->
    <section class="cs-services" id="services">
      <div class="cs-container">
        <h2>Our Services</h2>
        <p class="cs-subtitle">Comprehensive cleaning solutions tailored to your needs</p>
        <div class="cs-services-grid">
          <div class="cs-service-card">
            <div class="cs-icon">🏠</div>
            <h3>House Cleaning</h3>
            <p>Complete house cleaning including bedrooms, bathrooms, kitchen, and living areas.</p>
          </div>
          <div class="cs-service-card">
            <div class="cs-icon">🏢</div>
            <h3>Office Cleaning</h3>
            <p>Professional office and commercial space cleaning to maintain a clean work environment.</p>
          </div>
          <div class="cs-service-card">
            <div class="cs-icon">🧼</div>
            <h3>Deep Cleaning</h3>
            <p>Thorough deep cleaning services for carpets, upholstery, and hard-to-reach areas.</p>
          </div>
          <div class="cs-service-card">
            <div class="cs-icon">🪟</div>
            <h3>Window Cleaning</h3>
            <p>Professional window and glass cleaning for sparkling clean windows inside and out.</p>
          </div>
          <div class="cs-service-card">
            <div class="cs-icon">🧹</div>
            <h3>Move-In/Out Cleaning</h3>
            <p>Complete cleaning services for moving in or out of your property.</p>
          </div>
          <div class="cs-service-card">
            <div class="cs-icon">🌿</div>
            <h3>Eco-Friendly Cleaning</h3>
            <p>Sustainable and eco-friendly cleaning solutions for a healthier home.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Pricing Section -->
    <section class="cs-pricing" id="pricing">
      <div class="cs-container">
        <h2>Our Pricing</h2>
        <p class="cs-subtitle">Transparent and affordable pricing for all services</p>
        <div class="cs-pricing-grid">
          <div class="cs-pricing-card">
            <h3>Basic</h3>
            <div class="cs-price">$99</div>
            <p class="cs-price-desc">per service</p>
            <ul class="cs-features">
              <li>✓ House Cleaning</li>
              <li>✓ 2 Hours Service</li>
              <li>✓ Standard Supplies</li>
              <li>✗ Deep Cleaning</li>
            </ul>
            <button class="cs-btn-secondary">Choose Plan</button>
          </div>
          <div class="cs-pricing-card cs-featured">
            <div class="cs-badge">Most Popular</div>
            <h3>Professional</h3>
            <div class="cs-price">$199</div>
            <p class="cs-price-desc">per service</p>
            <ul class="cs-features">
              <li>✓ House Cleaning</li>
              <li>✓ 4 Hours Service</li>
              <li>✓ Premium Supplies</li>
              <li>✓ Deep Cleaning</li>
            </ul>
            <button class="cs-btn-primary">Choose Plan</button>
          </div>
          <div class="cs-pricing-card">
            <h3>Premium</h3>
            <div class="cs-price">$349</div>
            <p class="cs-price-desc">per service</p>
            <ul class="cs-features">
              <li>✓ All Services</li>
              <li>✓ 6+ Hours Service</li>
              <li>✓ Eco-Friendly Supplies</li>
              <li>✓ Unlimited Add-ons</li>
            </ul>
            <button class="cs-btn-secondary">Choose Plan</button>
          </div>
        </div>
      </div>
    </section>

    <!-- Testimonials Section -->
    <section class="cs-testimonials" id="testimonials">
      <div class="cs-container">
        <h2>What Our Clients Say</h2>
        <p class="cs-subtitle">Real reviews from satisfied customers</p>
        <div class="cs-testimonials-grid">
          <div class="cs-testimonial-card">
            <div class="cs-stars">★★★★★</div>
            <p>"Excellent service! The team was professional and thorough. My house looks amazing!"</p>
            <p class="cs-author">- Sarah Johnson</p>
          </div>
          <div class="cs-testimonial-card">
            <div class="cs-stars">★★★★★</div>
            <p>"Best cleaning service I've used. Very reliable and always on time. Highly recommended!"</p>
            <p class="cs-author">- Michael Chen</p>
          </div>
          <div class="cs-testimonial-card">
            <div class="cs-stars">★★★★★</div>
            <p>"The attention to detail is incredible. They go above and beyond every time."</p>
            <p class="cs-author">- Emily Rodriguez</p>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="cs-cta">
      <div class="cs-container">
        <h2>Ready for a Cleaner Home?</h2>
        <p>Get your free quote today and experience the CleanPro difference</p>
        <button class="cs-btn-primary">Get Free Quote</button>
      </div>
    </section>

    <!-- Footer -->
    <footer class="cs-footer">
      <div class="cs-container">
        <div class="cs-footer-content">
          <div class="cs-footer-section">
            <h4>CleanPro Services</h4>
            <p>Professional cleaning services for residential and commercial properties.</p>
          </div>
          <div class="cs-footer-section">
            <h4>Quick Links</h4>
            <ul>
              <li><a href="#services">Services</a></li>
              <li><a href="#pricing">Pricing</a></li>
              <li><a href="#testimonials">Testimonials</a></li>
            </ul>
          </div>
          <div class="cs-footer-section">
            <h4>Contact</h4>
            <p>📞 (555) 123-4567</p>
            <p>📧 info@cleanpro.com</p>
          </div>
        </div>
        <div class="cs-footer-bottom">
          <p>&copy; 2024 CleanPro Services. All rights reserved.</p>
        </div>
      </div>
    </footer>
  `,
  css: `
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }

    body {
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      line-height: 1.6;
      color: #0f172a;
    }

    .cs-container {
      max-width: 1200px;
      margin: 0 auto;
      padding: 0 20px;
    }

    /* Header */
    .cs-header {
      background: #fff;
      border-bottom: 1px solid #e2e8f0;
      padding: 16px 0;
      position: sticky;
      top: 0;
      z-index: 50;
    }

    .cs-header .cs-container {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .cs-logo {
      font-size: 24px;
      font-weight: 800;
      color: #0f172a;
    }

    .cs-nav {
      display: flex;
      gap: 32px;
      align-items: center;
    }

    .cs-nav a {
      color: #475569;
      text-decoration: none;
      font-weight: 500;
      font-size: 14px;
      transition: color 0.2s;
    }

    .cs-nav a:hover {
      color: #2563eb;
    }

    .cs-cta-btn {
      padding: 10px 20px;
      background: #2563eb;
      color: #fff;
      border: none;
      border-radius: 6px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s;
    }

    .cs-cta-btn:hover {
      background: #1d4ed8;
    }

    /* Hero Section */
    .cs-hero {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: #fff;
      padding: 120px 20px;
      text-align: center;
    }

    .cs-hero h1 {
      font-size: 48px;
      font-weight: 800;
      margin-bottom: 16px;
      line-height: 1.2;
    }

    .cs-hero p {
      font-size: 20px;
      color: rgba(255, 255, 255, 0.9);
      margin-bottom: 32px;
      max-width: 600px;
      margin-left: auto;
      margin-right: auto;
    }

    /* Buttons */
    .cs-btn-primary {
      padding: 14px 28px;
      background: #fff;
      color: #667eea;
      border: none;
      border-radius: 8px;
      font-weight: 600;
      font-size: 16px;
      cursor: pointer;
      transition: all 0.2s;
    }

    .cs-btn-primary:hover {
      transform: translateY(-2px);
      box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
    }

    .cs-btn-secondary {
      padding: 12px 24px;
      background: #f1f5f9;
      color: #667eea;
      border: 2px solid #667eea;
      border-radius: 8px;
      font-weight: 600;
      font-size: 14px;
      cursor: pointer;
      transition: all 0.2s;
    }

    .cs-btn-secondary:hover {
      background: #667eea;
      color: #fff;
    }

    /* Services Section */
    .cs-services {
      padding: 80px 20px;
      background: #f8fafc;
    }

    .cs-services h2 {
      font-size: 36px;
      font-weight: 800;
      text-align: center;
      margin-bottom: 12px;
      color: #0f172a;
    }

    .cs-subtitle {
      text-align: center;
      font-size: 18px;
      color: #64748b;
      margin-bottom: 48px;
    }

    .cs-services-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 32px;
      max-width: 1200px;
      margin: 0 auto;
    }

    .cs-service-card {
      background: #fff;
      padding: 32px;
      border-radius: 12px;
      border: 1px solid #e2e8f0;
      text-align: center;
      transition: all 0.3s;
    }

    .cs-service-card:hover {
      transform: translateY(-4px);
      box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);
    }

    .cs-icon {
      font-size: 48px;
      margin-bottom: 16px;
    }

    .cs-service-card h3 {
      font-size: 20px;
      font-weight: 700;
      margin-bottom: 12px;
      color: #0f172a;
    }

    .cs-service-card p {
      font-size: 16px;
      color: #64748b;
      line-height: 1.6;
    }

    /* Pricing Section */
    .cs-pricing {
      padding: 80px 20px;
      background: #fff;
    }

    .cs-pricing h2 {
      font-size: 36px;
      font-weight: 800;
      text-align: center;
      margin-bottom: 12px;
      color: #0f172a;
    }

    .cs-pricing-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 32px;
      max-width: 1200px;
      margin: 0 auto;
      margin-top: 48px;
    }

    .cs-pricing-card {
      background: #f8fafc;
      padding: 40px;
      border-radius: 12px;
      border: 2px solid #e2e8f0;
      text-align: center;
      transition: all 0.3s;
      position: relative;
    }

    .cs-pricing-card.cs-featured {
      background: #fff;
      border-color: #667eea;
      transform: scale(1.05);
      box-shadow: 0 20px 40px rgba(102, 126, 234, 0.2);
    }

    .cs-badge {
      position: absolute;
      top: -12px;
      left: 50%;
      transform: translateX(-50%);
      background: #667eea;
      color: #fff;
      padding: 6px 16px;
      border-radius: 20px;
      font-size: 12px;
      font-weight: 600;
    }

    .cs-pricing-card h3 {
      font-size: 24px;
      font-weight: 700;
      margin-bottom: 16px;
      margin-top: 12px;
      color: #0f172a;
    }

    .cs-price {
      font-size: 48px;
      font-weight: 800;
      color: #667eea;
      margin-bottom: 8px;
    }

    .cs-price-desc {
      color: #64748b;
      margin-bottom: 32px;
      font-size: 14px;
    }

    .cs-features {
      list-style: none;
      margin-bottom: 32px;
      text-align: left;
    }

    .cs-features li {
      padding: 12px 0;
      border-bottom: 1px solid #e2e8f0;
      color: #475569;
      font-size: 14px;
    }

    .cs-features li:last-child {
      border-bottom: none;
    }

    /* Testimonials Section */
    .cs-testimonials {
      padding: 80px 20px;
      background: #f8fafc;
    }

    .cs-testimonials h2 {
      font-size: 36px;
      font-weight: 800;
      text-align: center;
      margin-bottom: 12px;
      color: #0f172a;
    }

    .cs-testimonials-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 32px;
      max-width: 1200px;
      margin: 0 auto;
      margin-top: 48px;
    }

    .cs-testimonial-card {
      background: #fff;
      padding: 32px;
      border-radius: 12px;
      border: 1px solid #e2e8f0;
    }

    .cs-stars {
      color: #fbbf24;
      margin-bottom: 16px;
      font-size: 16px;
    }

    .cs-testimonial-card p {
      font-size: 16px;
      color: #64748b;
      margin-bottom: 16px;
      line-height: 1.6;
    }

    .cs-author {
      font-weight: 600;
      color: #0f172a;
      font-size: 14px;
    }

    /* CTA Section */
    .cs-cta {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: #fff;
      padding: 80px 20px;
      text-align: center;
    }

    .cs-cta h2 {
      font-size: 36px;
      font-weight: 800;
      margin-bottom: 16px;
    }

    .cs-cta p {
      font-size: 18px;
      color: rgba(255, 255, 255, 0.9);
      margin-bottom: 32px;
    }

    /* Footer */
    .cs-footer {
      background: #0f172a;
      color: #fff;
      padding: 60px 20px 20px;
    }

    .cs-footer-content {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 40px;
      margin-bottom: 40px;
      max-width: 1200px;
      margin-left: auto;
      margin-right: auto;
    }

    .cs-footer-section h4 {
      font-size: 16px;
      font-weight: 700;
      margin-bottom: 16px;
    }

    .cs-footer-section p {
      font-size: 14px;
      color: #cbd5e1;
      line-height: 1.8;
    }

    .cs-footer-section ul {
      list-style: none;
    }

    .cs-footer-section ul li {
      margin-bottom: 12px;
    }

    .cs-footer-section ul li a {
      color: #cbd5e1;
      text-decoration: none;
      transition: color 0.2s;
    }

    .cs-footer-section ul li a:hover {
      color: #fff;
    }

    .cs-footer-bottom {
      border-top: 1px solid #334155;
      padding-top: 20px;
      text-align: center;
      color: #94a3b8;
      font-size: 14px;
    }

    /* Responsive */
    @media (max-width: 768px) {
      .cs-nav {
        gap: 16px;
      }

      .cs-nav a {
        display: none;
      }

      .cs-hero h1 {
        font-size: 32px;
      }

      .cs-services-grid,
      .cs-pricing-grid,
      .cs-testimonials-grid {
        grid-template-columns: 1fr;
      }

      .cs-pricing-card.cs-featured {
        transform: scale(1);
      }

      .cs-footer-content {
        grid-template-columns: 1fr;
        gap: 20px;
      }
    }
  `,
  thumbnailUrl: 'https://flownect-media-dev.s3.us-west-1.amazonaws.com/templates-images/cleaning-services.png',
  blocks: [],
};